import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const MAX_SEC = 600;
const mime = () => ["audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

export function VoiceReportButton({ kind, value, onText, testid }) {
  const [state, setState] = useState("idle");
  const [sec, setSec] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);
  useEffect(() => { if (sec >= MAX_SEC && state === "rec") stop(); }, [sec, state]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (blob) => {
    setState("busy");
    try {
      const fd = new FormData();
      fd.append("file", blob, blob.type.includes("mp4") ? "audio.mp4" : "audio.webm");
      fd.append("kind", kind);
      const { data } = await api.post("/ai/voice-report", fd, { timeout: 180000 });
      onText(value?.trim() ? `${value.trim()}\n\n${data.text}` : data.text);
      toast.success("Rapporto scritto dall'audio: controlla e modifica se serve");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const m = mime();
      const r = new MediaRecorder(stream, m ? { mimeType: m } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); send(new Blob(chunks, { type: r.mimeType || m || "audio/webm" })); };
      rec.current = r;
      r.start();
      setSec(0);
      setState("rec");
      timer.current = setInterval(() => setSec((s) => s + 1), 1000);
    } catch {
      toast.error("Microfono non disponibile: consenti l'accesso al microfono");
    }
  };
  const stop = () => { clearInterval(timer.current); rec.current?.stop(); };

  const mmss = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  if (state === "rec") return (
    <Button type="button" size="sm" className="rounded-full bg-red-600 hover:bg-red-700" onClick={stop} data-testid={`${testid}-stop`}>
      <Square className="h-4 w-4 fill-current" /> Ferma e scrivi ({mmss})<span className="ml-1 h-2 w-2 animate-pulse rounded-full bg-white" /></Button>
  );
  return (
    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={start} disabled={state === "busy"} data-testid={testid}>
      {state === "busy" ? <><Loader2 className="h-4 w-4 animate-spin" /> L'IA sta scrivendo il rapporto…</> : <><Mic className="h-4 w-4" /> Detta il rapporto a voce</>}
    </Button>
  );
}
